import React, { useState, useEffect } from 'react';
import { XMarkIcon, PaperAirplaneIcon, DocumentIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';

interface EmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  to?: string;
  subject?: string;
  body?: string;
  template?: 'intro' | 'quote' | 'followup' | 'custom';
  recipientName?: string;
}

interface EmailTemplate {
  subject: string;
  body: string;
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  isOpen,
  onClose,
  to = '',
  subject = '',
  body = '',
  template = 'custom',
  recipientName = ''
}) => {
  const [formData, setFormData] = useState({
    to: to,
    cc: '',
    bcc: '',
    subject: subject,
    body: body,
    priority: 'normal' as 'high' | 'normal' | 'low'
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Email templates
  const templates: Record<string, EmailTemplate> = {
    intro: {
      subject: 'Introduction and Next Steps',
      body: `<p>Hi ${recipientName || 'there'},</p>
             <p>Thank you for your interest in my AI and automation consulting services. I'm excited about the possibility of working together!</p>
             <p>I've attached my introduction deck which outlines:</p>
             <ul>
               <li>My background and expertise</li>
               <li>Services I provide</li>
               <li>Case studies and success stories</li>
               <li>Typical project timelines and approach</li>
             </ul>
             <p>I'd love to schedule a brief call to discuss your specific needs and how I can help achieve your goals.</p>
             <p>Please let me know a few times that work for you, and I'll send over a calendar invite.</p>
             <p>Best regards,<br>Jean Bilbao</p>`
    },
    quote: {
      subject: 'Project Proposal and Quote',
      body: `<p>Hi ${recipientName || 'there'},</p>
             <p>Thank you for taking the time to discuss your project requirements with me.</p>
             <p>Based on our conversation, I've prepared a detailed proposal that includes:</p>
             <ul>
               <li>Project scope and deliverables</li>
               <li>Timeline and milestones</li>
               <li>Investment breakdown</li>
               <li>Terms and conditions</li>
             </ul>
             <p>I'm confident this solution will address your needs and deliver significant value to your organization.</p>
             <p>Please review the attached proposal and let me know if you have any questions or would like to discuss any aspects in more detail.</p>
             <p>I look forward to the opportunity to work with you!</p>
             <p>Best regards,<br>Jean Bilbao</p>`
    },
    followup: {
      subject: 'Following Up on Our Conversation',
      body: `<p>Hi ${recipientName || 'there'},</p>
             <p>I wanted to follow up on our recent conversation about your AI and automation needs.</p>
             <p>I've been thinking about your specific challenges and believe I can provide significant value through:</p>
             <ul>
               <li>Process automation to reduce manual work</li>
               <li>AI implementation for better decision making</li>
               <li>System integration for improved efficiency</li>
             </ul>
             <p>Would you be available for a brief call this week to discuss the next steps?</p>
             <p>I'm available [insert your availability] and happy to work around your schedule.</p>
             <p>Best regards,<br>Jean Bilbao</p>`
    }
  };

  // Load template when component opens
  useEffect(() => {
    if (isOpen && template && template !== 'custom' && templates[template]) {
      const templateData = templates[template];
      setFormData(prev => ({
        ...prev,
        subject: templateData.subject,
        body: templateData.body,
        to: to
      }));
    } else if (isOpen) {
      setFormData(prev => ({
        ...prev,
        to: to,
        subject: subject,
        body: body
      }));
    }
  }, [isOpen, template, to, subject, body, recipientName]);

  const handleSend = async () => {
    setSending(true);
    setMessage(null);

    try {
      const password = localStorage.getItem('admin-password') || '';
      
      // Create FormData for file uploads
      const emailData = new FormData();
      emailData.append('to', formData.to);
      emailData.append('cc', formData.cc);
      emailData.append('bcc', formData.bcc);
      emailData.append('subject', formData.subject);
      emailData.append('body', formData.body);
      emailData.append('priority', formData.priority);

      // Add attachments
      attachments.forEach((file, index) => {
        emailData.append(`attachment_${index}`, file);
      });

      const res = await fetch('/api/admin/send-email', {
        method: 'POST',
        headers: {
          'x-admin-password': password
        },
        body: emailData
      });

      const result = await res.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Email sent successfully!' });
        setTimeout(() => {
          onClose();
          setMessage(null);
        }, 2000);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send email' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send email. Please check your configuration.' });
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-4 bg-black/60 overflow-y-auto">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-4xl my-4 flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-white text-lg sm:text-xl font-semibold">Compose Email</h3>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white p-1"
          >
            <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/40 text-green-300'
                : 'bg-red-500/20 border border-red-500/40 text-red-300'
            }`}>
              {message.text}
            </div>
          )}

          <div className="space-y-4">
            {/* Recipients */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">To *</label>
                <input
                  type="email"
                  value={formData.to}
                  onChange={(e) => setFormData(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="recipient@example.com"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">CC</label>
                <input
                  type="email"
                  value={formData.cc}
                  onChange={(e) => setFormData(prev => ({ ...prev, cc: e.target.value }))}
                  placeholder="cc@example.com"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">BCC</label>
                <input
                  type="email"
                  value={formData.bcc}
                  onChange={(e) => setFormData(prev => ({ ...prev, bcc: e.target.value }))}
                  placeholder="bcc@example.com"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* Subject and Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="sm:col-span-3">
                <label className="block text-gray-300 text-sm mb-2">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject"
                  className="w-full p-3 bg-gray-700 rounded-lg text-white placeholder-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full p-3 bg-gray-700 rounded-lg text-white"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* Email Body */}
            <div>
              <label className="block text-gray-300 text-sm mb-2">Message *</label>
              <div className="bg-gray-700 rounded-lg overflow-hidden">
                <RichTextEditor
                  content={formData.body}
                  onChange={(value) => setFormData(prev => ({ ...prev, body: value }))}
                  placeholder="Write your email message..."
                />
              </div>
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-gray-300 text-sm mb-2">Attachments</label>
              <div className="space-y-2">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="w-full p-3 bg-gray-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-600 file:text-white hover:file:bg-primary-700"
                />
                
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-600 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <DocumentIcon className="w-4 h-4 text-gray-300" />
                          <span className="text-sm text-gray-300">{file.name}</span>
                          <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 sm:p-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFormData(prev => ({ ...prev, ...templates.intro, to: formData.to }))}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm"
              >
                Load Intro Template
              </button>
              <button
                onClick={() => setFormData(prev => ({ ...prev, ...templates.quote, to: formData.to }))}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm"
              >
                Load Quote Template
              </button>
              <button
                onClick={() => setFormData(prev => ({ ...prev, ...templates.followup, to: formData.to }))}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm"
              >
                Load Follow-up Template
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !formData.to || !formData.subject || !formData.body}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg text-white text-sm disabled:opacity-50 flex items-center gap-2"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
